import { INotificationClient } from "@multiweb/clients/notifications";
import { INotificationGroup, INotificationItem } from "@multiweb/types/contracts";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { INotificationManager, NotificationListener } from "./INotificationManager";

export class NotificationManager implements INotificationManager {
    private readonly _client: INotificationClient;

    private _listeners: NotificationListener[] = [];

    private _groupsLoadingPromise?: Promise<void>;
    private _groups?: readonly Readonly<INotificationGroup>[];

    constructor(client: INotificationClient) {
        this._client = client;
    }

    public getNotificationItems(groupName: string, onlyUnread: boolean, startIndex: number, itemsCount: number, cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly Readonly<INotificationItem>[]> {
        return this._client.getNotificationItems(groupName, onlyUnread, startIndex, itemsCount, cancellationToken);
    }

    public async getNotificationGroups(cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly Readonly<INotificationGroup>[]> {
        if (this._groups !== undefined)
            return this._groups;

        if (!this._groupsLoadingPromise)
            this._groupsLoadingPromise = this._client.getNotificationGroups(cancellationToken).then(async groupTypes => {
                this._groups = await Promise.all(groupTypes.map(async group => ({
                    type: group,
                    unread: await this._client.existsUnreadNotification(group.name),
                })));
            });

        // řeší paralelní vícenásobné volání - v podstatě se jedná o zámek na kterém budou všechny asynchronní procesy čekat na jedno dočtení dat
        await this._groupsLoadingPromise;

        return this._groups;
    }

    public async setNotificationAsRead(notificationId: number, markAsRead: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        return this._client.setNotificationAsRead(notificationId, markAsRead, cancellationToken);
    }

    public async setGroupNotificationsAsRead(notificationGroupId: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        return this._client.setGroupNotificationsAsRead(notificationGroupId, cancellationToken);
    }

    private readonly _refreshNotifications = async () => {
        if (this._groups === undefined)
            await this.getNotificationGroups();

        let groupChanged = false;

        this._groups = await Promise.all(this._groups.map(async (group) => {
            const unreadExists = await this._client.existsUnreadNotification(group.type.name);
            if (group.unread !== unreadExists)
                groupChanged = true;

            return {
                type: group.type,
                unread: unreadExists,
            };
        }));

        if (groupChanged)
            this._listeners.forEach((listener) => listener());
    };

    public async addNotificationGroupListener(listener: NotificationListener): Promise<void> {
        if (this._listeners.length === 0)
            await this._client.subscribeNotificationChange(this._refreshNotifications);

        this._listeners.push(listener);
    }

    public async removeNotificationGroupListener(listener: NotificationListener): Promise<void> {
        this._listeners = this._listeners.splice(this._listeners.indexOf(listener), 1);

        if (this._listeners.length === 0)
            await this._client.unsubscribeNotificationChange(this._refreshNotifications);
    }
}
